import {
  AwardIcon,
  CheckCircleIcon,
  ClockIcon,
  StarIcon,
  XCircleIcon,
} from "@design-system/components/Icons";
import { trans } from "@mongez/localization";
import {
  useOfferQuickActions,
  useOfferStatus,
  usePurchaseOffer,
} from "../hooks";

export function OfferQuickActions() {
  const { getStatusInfo, isLoggedIn } = useOfferStatus();
  const {
    buttonText,
    priceText,
    priceSubtext,
    handleStartLearning,
    isFree,
  } = useOfferQuickActions();
  const { purchaseOffer } = usePurchaseOffer();

  const statusInfo = getStatusInfo();

  const renderIcon = () => {
    switch (statusInfo.icon) {
      case "CheckCircle":
        return <CheckCircleIcon className="h-5 w-5" />;
      case "Clock":
        return <ClockIcon className="h-5 w-5" />;
      case "XCircle":
        return <XCircleIcon className="h-5 w-5" />;
      case "Gift":
        return <StarIcon className="h-5 w-5" />;
      default:
        return <CheckCircleIcon className="h-5 w-5" />;
    }
  };

  return (
    <div className="sticky top-8 space-y-3 sm:space-y-4">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm sm:p-6">
        <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
          {trans("offers.quickActions")}
        </h3>

        {/* Price Section - Hidden when purchased or free */}
        {statusInfo.showPrice && !isFree && (
          <div className="mb-4 text-center sm:mb-6">
            <div className="mb-1 text-2xl font-bold text-gray-900 dark:text-gray-100 sm:text-3xl">
              {priceText}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">{priceSubtext}</div>
          </div>
        )}

        {/* Action/Status Section */}
        <div className="space-y-2 sm:space-y-3">
          {statusInfo.status === "purchased" ? (
            <>
              {/* Gamified Achievement UI for Purchased Offer */}
              <div className="relative overflow-hidden rounded-lg border-2 border-yellow-200 dark:border-yellow-700 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 p-4 sm:p-6">
                <div className="absolute right-2 top-2">
                  <div className="animate-pulse">
                    <AwardIcon className="h-6 w-6 text-yellow-500" />
                  </div>
                </div>
                <div className="text-center">
                  <div className="mb-2 flex justify-center">
                    <div className="rounded-full bg-yellow-100 dark:bg-yellow-800/30 p-3">
                      <CheckCircleIcon className="h-8 w-8 text-yellow-600" />
                    </div>
                  </div>
                  <h4 className="mb-1 text-lg font-bold text-yellow-800 dark:text-yellow-200">
                    {trans(statusInfo.label)}
                  </h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {trans("offers.readyToLearn")}
                  </p>
                </div>
              </div>
              <button
                className="mt-2 w-full transform rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-3 font-medium text-white transition-all hover:scale-[1.02] hover:from-green-700 hover:to-emerald-700 hover:shadow-lg"
                onClick={handleStartLearning}
              >
                {trans("startLearning")}
              </button>
            </>
          ) : statusInfo.status === "pending" ? (
            <>
              <div
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${statusInfo.color}`}
              >
                {renderIcon()}
                {trans(statusInfo.label)}
              </div>
              <div className="text-center text-sm text-gray-600 dark:text-gray-300">
                {trans("offers.paymentUnderReviewMessage")}
              </div>
            </>
          ) : statusInfo.status === "failed" ? (
            <>
              <div
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${statusInfo.color}`}
              >
                {renderIcon()}
                {trans(statusInfo.label)}
              </div>
              <div className="text-center text-sm text-gray-600 dark:text-gray-300">
                {trans("offers.paymentFailedMessage")}
              </div>
              <button
                onClick={purchaseOffer}
                className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-white transition-colors hover:bg-primary/90"
              >
                {trans("offers.tryAgain")}
              </button>
            </>
          ) : statusInfo.status === "expired" ? (
            <>
              <div
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${statusInfo.color}`}
              >
                {renderIcon()}
                {trans(statusInfo.label)}
              </div>
              <div className="text-center text-sm text-gray-600 dark:text-gray-300">
                {trans("offers.offerExpiredMessage")}
              </div>
            </>
          ) : statusInfo.status === "notStarted" ? (
            <>
              <div
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${statusInfo.color}`}
              >
                {renderIcon()}
                {trans(statusInfo.label)}
              </div>
              <div className="text-center text-sm text-gray-600 dark:text-gray-300">
                {trans("offers.offerNotStartedMessage")}
              </div>
            </>
          ) : isFree && isLoggedIn ? (
            <>
              <div className="flex items-center justify-center gap-2 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                {trans("offers.freeOfferAvailable")}
              </div>
              <button
                className="w-full transform rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-3 font-medium text-white transition-all hover:scale-[1.02] hover:from-green-700 hover:to-emerald-700 hover:shadow-lg"
                onClick={purchaseOffer}
              >
                {trans("offers.unlockForFree")}
              </button>
            </>
          ) : statusInfo.canPurchase ? (
            <button
              onClick={purchaseOffer}
              className="w-full rounded-lg bg-primary px-4 py-3 font-medium text-white transition-colors hover:bg-primary/90"
            >
              {buttonText}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
